import {
    Button,
    DataGrid,
    DataGridBody,
    DataGridCell,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridRow,
    Divider,
    Input,
    Label,
    Link,
    Radio,
    RadioGroup,
    createTableColumn,
    makeStyles,
    shorthands,
    tokens,
} from "@fluentui/react-components";
import {
    useContext,
    useState,
    useEffect,
    useMemo,
    useRef,
    useCallback,
} from "react";
import { Context } from "../../context";
import { TableDef } from "../../table_def";
import { getCurrentUser } from "../../services/login";
import { apiClient } from "../../services/api";
import { getCurrentFieldIdFromNew } from "../../workspace";
import {
    cvtLeafPart,
    cvtRawDataStatus,
    idRecord,
    idString,
    parseDateAsPath,
} from "../../db_utils";
import * as IC from "@fluentui/react-icons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

const useStyles = makeStyles({
    root: {
        height: "100%",
        overflowY: "auto",
        ...shorthands.flex(1),
        ...shorthands.padding("10px"),
        ...shorthands.gap("10px"),
        display: "grid",
        gridTemplateColumns: "2fr 7fr",
    },
    list: {
        display: "flex",
        overflowY: "auto",
        flexDirection: "column",
        backgroundColor: "#eee",
        ...shorthands.flex(1),
        ...shorthands.padding("10px"),
        ...shorthands.gap("10px"),
    },
    taskCard: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        ...shorthands.padding("10px"),
        ...shorthands.gap("10px"),
    },
});

const TaskCard = ({ task, curTask, onSelect, doRefresh }) => {
    let { db, svr, apiClient } = useContext(Context);

    const doSubmit = async () => {
        console.log("merge task:", task);
        await apiClient.releaseTask(task.id);
        // 生成pdf报告
        try {
            await fetch(`${svr}/gen_pdf_report/${idString(task.id)}`, {
                method: "GET",
                mode: "no-cors",
            });
        } catch (error) {
            console.warn('PDF generation service not available:', error);
            // Continue without PDF generation for now
        }
        alert("提交成功");
        doRefresh();
    };

    let styles = useStyles();
    return (
        <div
            className={styles.taskCard}
            style={{
                backgroundColor:
                    idString(task?.id) === idString(curTask?.id)
                        ? tokens.colorBrandBackground2
                        : "white",
            }}
        >
            <h4>编号:{task?.seq}</h4>
            <Button
                onClick={() => {
                    if (onSelect) {
                        onSelect(task);
                    }
                }}
            >
                打开复核
            </Button>
            <Button
                onClick={async () => {
                    await doSubmit();
                }}
            >
                复核完成
            </Button>
        </div>
    );
};

const SessionCard = ({ session, curSession, onSelect, doRefresh }) => {
    let { apiClient } = useContext(Context);

    const markReadyToRelease = async () => {
        console.log("marking session ready to release:", session);
        await apiClient.put(`/inspectionsession/${session.id}/review-status`, {
            reviewStatus: "ready_to_release"
        });
        alert("会话复核完成，待发布");
        doRefresh();
    };

    const doSubmit = async () => {
        console.log("releasing session:", session);
        await apiClient.post(`/session/${session.id}/release`, {});
        alert("会话已发布");
        doRefresh();
    };

    const getStatusBadge = (session) => {
        // Check if it's a session with in_recheck status (tasks need re-annotation)
        if (session?.reviewStatus === "in_recheck") {
            return { text: "重新标注中", color: tokens.colorPaletteLightTealBackground2 };
        }
        if (session?.reviewStatus === "ready_to_release") {
            return { text: "待发布", color: tokens.colorPaletteLightGreenBackground2 };
        }
        if (session?.reviewStatus === "released") {
            return { text: "已发布", color: tokens.colorNeutralBackground3 };
        }
        // Default: session with all tasks completed, ready for initial review
        return { text: "待复核", color: tokens.colorPaletteLightBlueBackground2 };
    };

    let styles = useStyles();
    const statusInfo = getStatusBadge(session);

    return (
        <div
            className={styles.taskCard}
            style={{
                backgroundColor:
                    session?.id === curSession?.id
                        ? tokens.colorBrandBackground2
                        : "white",
            }}
        >
            <h4>会话编号: {session?.seq}</h4>
            <div style={{ fontSize: "12px", color: tokens.colorNeutralForeground3 }}>
                {session?.sessionName}
            </div>
            <div style={{
                padding: "4px 8px",
                borderRadius: "4px",
                backgroundColor: statusInfo.color,
                color: tokens.colorNeutralForeground1,
                fontSize: "12px",
                textAlign: "center",
                marginTop: "8px",
                fontWeight: 500
            }}>
                {statusInfo.text}
            </div>
            <div style={{ fontSize: "12px", marginTop: "8px" }}>
                任务数: {session?.taskCount || 0}
            </div>
            <Button
                onClick={() => {
                    if (onSelect) {
                        onSelect(session);
                    }
                }}
                style={{ marginTop: "8px" }}
            >
                查看详情
            </Button>
            {session?.reviewStatus !== "ready_to_release" && session?.reviewStatus !== "released" && (
                <Button
                    onClick={async () => {
                        await markReadyToRelease();
                    }}
                    appearance="primary"
                >
                    复核完成
                </Button>
            )}
            {session?.reviewStatus === "ready_to_release" && (
                <Button
                    onClick={async () => {
                        await doSubmit();
                    }}
                    appearance="primary"
                >
                    发布会话
                </Button>
            )}
        </div>
    );
};

const TaskList = ({ onSelect }) => {
    let { apiClient, currentField } = useContext(Context);
    let [sessions, setSessions] = useState([]);
    let [tasks, setTasks] = useState([]);
    let [session, setSession] = useState(null);
    let [task, setTask] = useState(null);
    let user = getCurrentUser();
    let [idx, setIdx] = useSearchParams();
    let [viewMode, setViewMode] = useState(() => idx.get("view") === "tasks" ? "tasks" : "sessions"); // "sessions" or "tasks"

    const listSessions = async () => {
        try {
            // Get sessions where ALL tasks are completed and ready for recheck/release
            const items = await apiClient.get('/inspectionsession?reviewStatus=recheck_or_ready');
            setSessions(items || []);
        } catch (error) {
            console.error("Failed to load sessions:", error);
            setSessions([]);
        }
    };

    const listTask = async () => {
        try {
            const params = {
                status: 'recheck'
            };
            if (user.role?.toLowerCase() !== "admin" && user.role?.toLowerCase() !== "field_admin") {
                params.recheckerId = user.id;
            }
            // Get current field from Context
            if (currentField && currentField.id) {
                params.fieldId = currentField.id;
            }
            let items = await apiClient.getTasks(params);
            // Optional: filter by sessionId from querystring (when launched from annotation page)
            const sid = idx.get('sessionId');
            if (sid) {
                items = (items || []).filter(t => String(t.session?.id || t.sessionId) === String(sid));
            }
            setTasks(items || []);
        } catch (error) {
            console.error("Failed to load recheck tasks:", error);
            setTasks([]);
        }
    };

    const doRefresh = () => {
        if (viewMode === "sessions") {
            listSessions();
        } else {
            listTask();
        }
    };

    useEffect(() => {
        const requestedView = idx.get("view");
        if (requestedView === "tasks" && viewMode !== "tasks") {
            setViewMode("tasks");
        } else if (requestedView === "sessions" && viewMode !== "sessions") {
            setViewMode("sessions");
        }
    }, [idx, viewMode]);

    useEffect(() => {
        let idx_ = idx.get("idx");
        let taskId = idx.get("taskId");
        let sessionId = idx.get("sessionId");

        if (taskId && tasks.length > 0) {
            const selectedTask = tasks.find(t => String(t.id) === String(taskId));
            if (selectedTask) {
                setTask(selectedTask);
                if (viewMode !== "tasks") {
                    setViewMode("tasks");
                }
                return;
            }
        }

        if (sessionId && sessions.length > 0) {
            const selectedSession = sessions.find(s => String(s.id) === String(sessionId));
            if (selectedSession && viewMode === "sessions") {
                setSession(selectedSession);
                return;
            }
        }

        if (idx_ != null) {
            if (viewMode === "sessions" && sessions[idx_]) {
                setSession(sessions[idx_]);
            } else if (viewMode === "tasks" && tasks[idx_]) {
                setTask(tasks[idx_]);
            }
        }
    }, [idx, sessions, tasks, viewMode]);

    useEffect(() => {
        if (apiClient) {
            listSessions(); // Load sessions by default
            listTask(); // Also load tasks
        }
    }, [apiClient, currentField]);

    useEffect(() => {
        if (viewMode === "sessions" && session && onSelect) {
            onSelect(session);
        } else if (viewMode === "tasks" && task && onSelect) {
            onSelect(task);
        }
    }, [session, task, viewMode]);

    let styles = useStyles();
    return (
        <div className={styles.list}>
            <div style={{
                background: "white",
                padding: 10,
                boxShadow: tokens.shadow8,
                display: "flex",
                flexDirection: "column",
                gap: "10px"
            }}>
                <h3>缺陷复核</h3>
                <div style={{ display: "flex", gap: "10px" }}>
                    <Button
                        appearance={viewMode === "sessions" ? "primary" : "outline"}
                        onClick={() => {
                            setViewMode("sessions");
                            setIdx({ view: "sessions" });
                        }}
                        size="small"
                    >
                        会话视图 ({sessions.length})
                    </Button>
                    <Button
                        appearance={viewMode === "tasks" ? "primary" : "outline"}
                        onClick={() => {
                            setViewMode("tasks");
                            setIdx({ view: "tasks" });
                        }}
                        size="small"
                    >
                        任务视图 ({tasks.length})
                    </Button>
                </div>
            </div>

            {viewMode === "sessions" ? (
                <>
                    {sessions.map((s, i) => {
                        return (
                            <SessionCard
                                doRefresh={doRefresh}
                                key={i}
                                session={s}
                                curSession={session}
                                onSelect={(s) => {
                                    setIdx({ view: "sessions", idx: i });
                                    setSession(s);
                                }}
                            />
                        );
                    })}
                </>
            ) : (
                <>
                    {tasks.map((t, i) => {
                        return (
                            <TaskCard
                                doRefresh={doRefresh}
                                key={i}
                                task={t}
                                curTask={task}
                                onSelect={(t) => {
                                    setIdx({ view: "tasks", idx: i });
                                    setTask(t);
                                }}
                            />
                        );
                    })}
                </>
            )}
        </div>
    );
};

const getFileUrl = async (taskId, rawDataId) => {
    try {
        const apiEndpoint = process.env.REACT_APP_API_ENDPOINT || 'http://localhost:5001';
        const response = await fetch(`${apiEndpoint}/api/file/url/rawdata/${taskId}/${rawDataId}`, {
            headers: {
                "Authorization": `Bearer ${localStorage.getItem('token')}`
            }
        });
        const data = await response.json();
        return data.url;
    } catch (error) {
        console.error('Error getting file URL:', error);
        return null;
    }
};

const getFrameDataUrl = async (frameDataId) => {
    try {
        const apiEndpoint = process.env.REACT_APP_API_ENDPOINT || 'http://localhost:5001';
        const response = await fetch(`${apiEndpoint}/api/file/url/framedata/${frameDataId}`, {
            headers: {
                "Authorization": `Bearer ${localStorage.getItem('token')}`
            }
        });
        const data = await response.json();
        return data.url;
    } catch (error) {
        console.error('Error getting frame data URL:', error);
        return null;
    }
};

const TurbineRecheckList = ({ task }) => {
    let [items, setItems] = useState([]);
    let { db, oss, svr, apiClient } = useContext(Context);
    let [refresh, setRefresh] = useState(0);
    let nav = useNavigate();

    const doRefresh = () => {
        setRefresh(Date.now());
    };

    const listItem = async () => {
        try {
            const rawData = await apiClient.getRawData(task?.id);
            setItems(rawData || []);
        } catch (error) {
            console.error('Failed to load raw data:', error);
            setItems([]);
        }
    };

    useEffect(() => {
        if (apiClient && task) listItem();
    }, [apiClient, task, refresh]);

    useEffect(() => {
        let isPreprocessing = false;
        for (const item of items) {
            if (item.status == "preprocessing") {
                isPreprocessing = true;
                break;
            }
        }
        if (isPreprocessing) {
            let handle = setInterval(() => {
                console.log("refresh raw data status");
                doRefresh();
            }, 1000);
            return () => {
                console.log("clear raw_data callback handle");
                clearInterval(handle);
            };
        }
    }, [items]);

    const columns = [
        createTableColumn({
            columnId: "name",
            renderHeaderCell: () => {
                return "文件名";
            },
            renderCell: (item) => {
                let name = item.name;
                // let name = item.name.substring(item.name.length - 10);
                // if (item.name.length > 10) {
                //     name = "..." + name;
                // }
                const handleDownload = async () => {
                    const url = await getFileUrl(task?.id, item?.id);
                    if (url) {
                        window.open(url, '_blank');
                    }
                };
                return <Link onClick={handleDownload}>{name}</Link>;
            },
        }),
        createTableColumn({
            columnId: "turbine",
            renderHeaderCell: () => {
                return "所属风机";
            },
            renderCell: (item) => {
                return item.leaf?.turbine?.name || "未知";
            },
        }),
        createTableColumn({
            columnId: "attatch_id",
            renderHeaderCell: () => {
                return "挂载点";
            },
            renderCell: (item) => {
                return item.leaf?.attachId || item.leaf?.attach_id || "未知";
            },
        }),
        createTableColumn({
            columnId: "part",
            renderHeaderCell: () => {
                return "叶片部分";
            },
            renderCell: (item) => {
                return cvtLeafPart(item.part);
            },
        }),
        createTableColumn({
            columnId: "updated_by",
            renderHeaderCell: () => {
                return "上传人员";
            },
            renderCell: (item) => {
                return item.uploadedBy?.name || item.uploaded_by?.name || "未知";
            },
        }),
        createTableColumn({
            columnId: "updated_at",
            renderHeaderCell: () => {
                return "更新时间";
            },
            renderCell: (item) => {
                return new Date(item.uploaded_at).toLocaleString();
            },
        }),
        createTableColumn({
            columnId: "status",
            renderHeaderCell: () => {
                return "当前状态";
            },
            renderCell: (item) => {
                return cvtRawDataStatus(item.status);
            },
        }),

        createTableColumn({
            columnId: "ops",
            renderHeaderCell: () => {
                return "操作";
            },
            renderCell: (item) => {
                if (item.status == "uploaded") {
                    return (
                        <Button
                            appearance="subtle"
                            icon={<IC.ArchiveRegular />}
                            onClick={() => {
                                // let handle = setInterval(
                                //     () => doRefresh(),
                                //     1000
                                // );
                                fetch(
                                    `${svr}/raw/process/${idString(item.id)}`,
                                    {
                                        method: "POST",
                                        mode: "no-cors",
                                    },
                                )
                                    .then(() => {
                                        // alert("预处理完成");
                                    })
                                    .catch((e) => {
                                        console.error(e);
                                        alert("预处理服务未启动 (端口9002) - 视频处理功能需要主后端服务运行");
                                    })
                                    .finally(() => {
                                        doRefresh();
                                        // clearInterval(handle);
                                    });
                            }}
                        >
                            预处理
                        </Button>
                    );
                }

                if (item.status == "completed") {
                    return (
                        <Button
                            appearance="subtle"
                            icon={<IC.CheckRegular />}
                            onClick={() => {
                                nav(`/task/recheck/${idString(item.id)}`);
                            }}
                        >
                            复核
                        </Button>
                    );
                }
            },
        }),
    ];

    return (
        <div style={{ padding: 10, gap: 10 }}>
            {/* {JSON.stringify(task)} */}
            {/* {JSON.stringify(items)} */}
            <h3>任务关联数据</h3>
            <DataGrid columns={columns} items={items}>
                <DataGridHeader>
                    <DataGridRow>
                        {({ renderHeaderCell }, i) => (
                            <DataGridHeaderCell key={i}>
                                {renderHeaderCell()}
                            </DataGridHeaderCell>
                        )}
                    </DataGridRow>
                </DataGridHeader>
                <DataGridBody>
                    {({ item }) => (
                        <DataGridRow key={item.seq}>
                            {({ renderCell }) => (
                                <DataGridCell>{renderCell(item)}</DataGridCell>
                            )}
                        </DataGridRow>
                    )}
                </DataGridBody>
            </DataGrid>
        </div>
    );
};

export const TaskRecheck = () => {
    let styles = useStyles();
    let [task, setTask] = useState(null);
    return (
        <div className={styles.root}>
            <TaskList onSelect={(t) => setTask(t)} />
            <TurbineRecheckList task={task} />
        </div>
    );
};

const useStylesRecheckDetail = makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
    },
    slice: {
        position: "absolute",
        height: "100%",
        width: "5px",
        top: 0,
        opacity: 0.5,
        ":hover": {
            opacity: 1.0,
        },
    },
});

export const TagCard = ({ idx, tag, onSelect, curTag, color }) => {
    let isCurrent = tag.seq === curTag?.seq;
    return (
        <div
            style={{
                display: "grid",
                width: "100%",
                gridTemplateColumns: "1fr 1fr 1fr",
                alignItems: "center",
            }}
        >
            <Radio key={tag.seq} value={idx} />
            <span>{tag.name}</span>
            <div
                style={{
                    // border: "1px solid " + tag.color,
                    borderRadius: 10,
                    height: "40%",
                    opacity: isCurrent ? 1.0 : 0.3,
                    boxShadow: isCurrent ? tokens.shadow8 : tokens.shadow4,
                    backgroundColor: tag.color,
                }}
            ></div>
        </div>
    );
    return (
        <div
            style={{
                color: color,
                display: "flex",
                padding: 5,
                paddingLeft: 10,
                paddingRight: 10,
                borderRadius: 100,
                border: "1px solid " + tag.color,
                backgroundColor:
                    tag.seq === curTag?.seq ? tag.color : undefined,
            }}
            onClick={() => {
                tag.seq === curTag?.seq ? onSelect(null) : onSelect(tag);
            }}
        >
            <span>{tag.name}</span>
        </div>
    );
};

export const TaskRecheckDetail = () => {
    let { rawDataId } = useParams();
    let { db, oss } = useContext(Context);
    let [items, setItems] = useState([]);
    let [idx, setIdx] = useState(0);
    let [defIdx, setDefIdx] = useState(null);
    let [defHighlightIdx, setDefHighlightIdx] = useState(null);
    let [rawData, setRawData] = useState(null);
    let [tags, setTags] = useState([]);
    let [curTag, setCurTag] = useState(null);
    let nav = useNavigate();
    let [refresh, setRefresh] = useState(0);
    let [imgRect, setImgRect] = useState(null);
    let [labelRect, setLabelRect] = useState(null);
    let [mousePos, setMousePos] = useState(null);
    let imgRef = useRef(null);
    let labelRef = useRef(null);
    let [clicks, setClicks] = useState([]);
    let [defections, setDefections] = useState([]);
    let [frameUrl, setFrameUrl] = useState(null);

    const handleEscape = useCallback(
        (event) => {
            if (event.key == "Escape") {
                setClicks([]);
            }
            if (event.key == "n") {
                if (idx + 1 < items.length) setIdx(idx + 1);
            }
            if (event.key == "p") {
                if (idx > 0) setIdx(idx - 1);
            }
        },
        [idx, items],
    );

    let previewRectStyle = useMemo(() => {
        if (clicks.length == 1 && curTag && mousePos) {
            let left = mousePos?.x > clicks?.[0].x ? clicks[0].x : mousePos?.x;
            let top = mousePos?.y > clicks?.[0].y ? clicks[0].y : mousePos?.y;
            let width = Math.abs(mousePos?.x - clicks[0]?.x);
            let height = Math.abs(mousePos?.y - clicks[0]?.y);
            return {
                position: "absolute",
                left: `${left * 100}%`,
                top: `${top * 100}%`,
                width: `${width * 100}%`,
                height: `${height * 100}%`,
                backgroundColor: curTag.color,
                border: `1px dotted ${curTag.color}`,
                opacity: 0.6,
            };
        }
        return {
            position: "absolute",
            width: 0,
            height: 0,
            left: 0,
            height: 0,
        };
    }, [clicks, curTag, mousePos]);

    const doRefresh = () => {
        setRefresh(refresh + 1);
    };

    const listFrame = async () => {
        let [rets] = await db.query(
            `SELECT * FROM frame_data WHERE from_raw=type::thing($rawDataId) ORDER BY timestamp ASC`,
            {
                rawDataId,
            },
        );
        setItems(rets);
    };

    const getRawData = async () => {
        const [ret] = await db.query(
            `SELECT * FROM ONLY type::thing($rawDataId) fetch leaf,leaf.model,leaf.model.manu,leaf.turbine,leaf.turbine.field_item`,
            {
                rawDataId,
            },
        );
        setRawData(ret);
    };

    const listTag = async () => {
        const [rets] = await db.query(`SELECT * FROM def_defection`);
        setTags(rets);
    };

    const listDefection = async () => {
        const [rets] = await db.query(
            `SELECT * FROM defection WHERE from_frame=type::thing($frameId) ORDER BY updated_at FETCH label`,
            {
                frameId: items[idx].id,
            },
        );
        setDefections(rets);
    };

    const doCheckFrame = async () => {
        await db.merge(idRecord(items[idx].id), { checked: true });
        doRefresh();
    };

    useEffect(() => {
        if (db && items?.[idx]) {
            if (!items[idx].checked) {
                doCheckFrame();
            }
        }
    }, [db, items, idx]);

    useEffect(() => {
        if (db && items?.[idx]) listDefection();
    }, [items, idx, db, refresh]);

    useEffect(() => {
        if (db) listTag();
        if (db && oss) listFrame();
        if (db && rawDataId) getRawData();
    }, [db, oss, rawDataId, refresh]);

    useEffect(() => {
        if (imgRef?.current) {
            const getRect = () => {
                let rect = imgRef.current.getBoundingClientRect();
                setImgRect(rect);
            };
            getRect();
            let handle = setInterval(getRect, 1000);
            return () => {
                clearInterval(handle);
            };
        }
    }, [imgRef]);

    useEffect(() => {
        if (labelRef?.current) {
            const getRect = () => {
                let rect = labelRef.current.getBoundingClientRect();
                setLabelRect(rect);
            };
            getRect();
            let handle = setInterval(getRect, 1000);
            return () => {
                clearInterval(handle);
            };
        }
    }, [labelRef]);

    useEffect(() => {
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("keydown", handleEscape);
        };
    }, [handleEscape]);

    const labelStyle = {
        backgroundColor: "gray",
        padding: 5,
        color: "white",
        textAlign: "center",
    };

    // Load frame URL when current item changes
    useEffect(() => {
        const loadFrameUrl = async () => {
            let item = items?.[idx];
            if (item?.id) {
                const url = await getFrameDataUrl(item.id);
                setFrameUrl(url);
            } else {
                setFrameUrl(null);
            }
        };
        loadFrameUrl();
    }, [items, idx]);

    let styles = useStylesRecheckDetail();

    const infoBar = (
        <div
            style={{
                flex: 1,
                display: "grid",
                gridTemplateColumns:
                    "auto auto auto auto auto auto auto auto auto auto",
                alignItems: "center",
                borderBottom: "0.5px solid gray",
            }}
        >
            <Label style={labelStyle}>风场</Label>
            <Label style={{ textAlign: "center" }}>
                {rawData?.leaf?.turbine?.field_item?.name}
            </Label>
            <Label style={labelStyle}>风机</Label>
            <Label style={{ textAlign: "center" }}>
                {rawData?.leaf?.turbine?.name}
            </Label>
            <Label style={labelStyle}>叶片</Label>
            <Label style={{ textAlign: "center" }}>
                {idString(rawData?.leaf?.attachId || rawData?.leaf?.attach_id)}/{rawData?.leaf?.seq}
            </Label>
            <Label style={labelStyle}>部位</Label>
            <Label style={{ textAlign: "center" }}>
                {rawData?.part && cvtLeafPart(rawData?.part)}
            </Label>
            <Label style={labelStyle}>长度</Label>
            <Label style={{ textAlign: "center" }}>
                {rawData?.leaf?.model?.length}米
            </Label>
        </div>
    );

    const labeler = (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
            }}
        >
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    backgroundColor: "#eee",
                    gap: 10,
                    borderBottom: "0.5px solid gray",
                }}
            >
                <Button
                    appearance="subtle"
                    onClick={() => {
                        nav(-1);
                    }}
                    icon={<IC.ArrowHookDownLeftFilled />}
                >
                    返回
                </Button>
                <Button
                    disabled={idx == 0}
                    appearance="subtle"
                    onClick={() => {
                        setIdx(0);
                    }}
                    icon={<IC.ArrowPreviousFilled />}
                />
                <Button
                    disabled={idx == 0}
                    onClick={() => {
                        setIdx(idx - 1);
                    }}
                    appearance="subtle"
                    icon={<IC.ChevronLeftFilled />}
                />
                <Button
                    disabled={idx + 1 >= items.length}
                    onClick={() => {
                        setIdx(idx + 1);
                    }}
                    appearance="subtle"
                    icon={<IC.ChevronRightFilled />}
                />
                <Button
                    disabled={idx + 1 >= items.length}
                    appearance="subtle"
                    onClick={() => {
                        setIdx(items.length - 1);
                    }}
                    icon={<IC.ArrowNextFilled />}
                />
                {infoBar}
            </div>
            <div
                ref={labelRef}
                style={{
                    position: "relative",
                    backgroundColor: "#aaa",
                    flex: 1,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                {mousePos && (
                    <div
                        style={{
                            position: "absolute",
                            right: 10,
                            bottom: 10,
                            height: 20,
                            zIndex: 9,
                            backgroundColor: "gray",
                            color: "white",
                            fontFamily: "monospace",
                        }}
                    >
                        {`${mousePos?.x.toFixed(2)}x${mousePos?.y.toFixed(2)}`}
                    </div>
                )}
                <div
                    style={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <img
                        ref={imgRef}
                        src={frameUrl}
                        style={{
                            zIndex: 10,
                            position: "absolute",
                            objectFit: "fill",
                            maxWidth: "100%",
                            maxHeight: "100%",
                            pointerEvents: "none",
                        }}
                    />
                    <div
                        onClick={async () => {
                            if (curTag) {
                                if (clicks.length == 0) {
                                    setClicks([mousePos]);
                                } else {
                                    if (db && items?.[idx] && curTag) {
                                        await db.create("defection", {
                                            from_frame: idRecord(items[idx].id),
                                            label: idRecord(curTag.id),
                                            label_id: curTag.seq,
                                            x:
                                                clicks[0].x > mousePos.x
                                                    ? mousePos.x
                                                    : clicks[0].x,
                                            y:
                                                clicks[0].y > mousePos.y
                                                    ? mousePos.y
                                                    : clicks[0].y,
                                            width: Math.abs(
                                                clicks[0].x - mousePos.x,
                                            ),
                                            height: Math.abs(
                                                clicks[0].y - mousePos.y,
                                            ),
                                            label_by: "human",
                                        });
                                        doRefresh();
                                    }
                                    setClicks([]);
                                }
                            } else {
                                setClicks([]);
                            }
                        }}
                        onMouseLeave={() => {
                            setMousePos(null);
                        }}
                        onMouseMove={(evt) => {
                            let { clientX, clientY, pageX, pageY } = evt;
                            let x = clientX - imgRect.x;
                            let y = clientY - imgRect.y;
                            let pos = {
                                x: x / imgRect.width,
                                y: y / imgRect.height,
                            };
                            setMousePos(pos);
                        }}
                        style={{
                            zIndex: 11,
                            position: "absolute",
                            top: imgRect?.top - labelRect?.top,
                            left: imgRect?.left - labelRect?.left,
                            width: imgRect?.width,
                            height: imgRect?.height,
                        }}
                    >
                        {defections.map((d, i) => {
                            let isHighlighted = i == defHighlightIdx;
                            return (
                                <div
                                    onMouseLeave={() => {
                                        setDefHighlightIdx(null);
                                    }}
                                    onMouseMove={() => {
                                        if (clicks.length == 0)
                                            setDefHighlightIdx(i);
                                    }}
                                    key={i}
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        position: "absolute",
                                        left: `${d.x * 100}%`,
                                        top: `${d.y * 100}%`,
                                        width: `${d.width * 100}%`,
                                        height: `${d.height * 100}%`,
                                        color: "white",
                                        fontSize: 24,
                                        backgroundColor: isHighlighted
                                            ? d.label.color
                                            : null,
                                        border: `2px dotted ${d.label.color}`,
                                        boxShadow: isHighlighted
                                            ? tokens.shadow64
                                            : null,
                                        opacity: 0.8,
                                    }}
                                >
                                    {isHighlighted && i + 1}
                                </div>
                            );
                        })}
                        <div style={previewRectStyle}></div>
                        <div
                            style={{
                                pointerEvents: "none",
                                position: "absolute",
                                height: curTag ? "100%" : 0,
                                left: `${mousePos?.x ? mousePos.x * 100 : 0}%`,
                                opacity: 0.5,
                                border: `${mousePos ? 0.1 : 0}px solid ${
                                    curTag?.color || "red"
                                }`,
                            }}
                        ></div>
                        <div
                            style={{
                                pointerEvents: "none",
                                position: "absolute",
                                width: curTag ? "100%" : 0,
                                opacity: 0.5,
                                top: `${mousePos?.y ? mousePos.y * 100 : 0}%`,
                                border: `${mousePos ? 0.1 : 0}px solid ${
                                    curTag?.color || "red"
                                }`,
                            }}
                        ></div>
                    </div>
                </div>
            </div>
        </div>
    );

    const tagZone = (
        <div
            style={{
                backgroundColor: "#eee",
                display: "grid",
                padding: 10,
                gridTemplateRows: "1fr 1fr",
                borderLeft: "0.5px solid gray",
            }}
        >
            <div style={{ display: "flex", flexDirection: "column" }}>
                <h4>标签选择</h4>
                <div
                    style={{
                        padding: 10,
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                        backgroundColor: "white",
                        border: "1px solid #ddd",
                        flex: 1,
                        borderRadius: 2,
                    }}
                >
                    <RadioGroup
                        onChange={(_, data) => {
                            setCurTag(tags[data.value]);
                        }}
                    >
                        {tags.map((tag, idx) => {
                            return (
                                <TagCard
                                    key={idx}
                                    tag={tag}
                                    idx={idx}
                                    curTag={curTag}
                                />
                            );
                        })}
                    </RadioGroup>
                </div>
            </div>
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    overflowY: "auto",
                }}
            >
                <h4>缺陷列表</h4>
                <div
                    style={{
                        flex: 1,
                        backgroundColor: "white",
                        border: "1px solid #ddd",
                        borderRadius: 2,
                        overflowY: "auto",
                        overflowX: "auto",
                    }}
                >
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr",
                            gap: 10,
                            padding: 10,
                            overflowY: "auto",
                        }}
                    >
                        {defections.map((d, i) => {
                            return (
                                <div
                                    onMouseEnter={() => {
                                        setDefHighlightIdx(i);
                                    }}
                                    onMouseLeave={() => {
                                        setDefHighlightIdx(null);
                                    }}
                                    onClick={async () => {
                                        await apiClient.delete(`/defection/${d.id}`);
                                        doRefresh();
                                    }}
                                    style={{
                                        borderRadius: 5,
                                        padding: 10,
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        height: 20,
                                        color:
                                            defHighlightIdx == i
                                                ? "white"
                                                : d.label.color,
                                        backgroundColor:
                                            defHighlightIdx == i
                                                ? d.label.color
                                                : "white",
                                        border: `1px solid ${d.label.color}`,
                                    }}
                                >
                                    <span>({i + 1})</span>
                                    <span>{d.label.name}</span>
                                    <IC.DeleteRegular fontSize={14} />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );

    const middle = (
        <div
            style={{
                flex: 1,
                display: "grid",
                gridTemplateColumns: "5fr 1fr",
            }}
        >
            {labeler}
            {tagZone}
        </div>
    );

    const track = (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 16fr",
                padding: 20,
                columnGap: 10,
                borderTop: "0.5px solid gray",
            }}
        >
            <span>爬行轨迹</span>
            <span>
                {idx + 1}/{items.length}
            </span>
            <span>{(items?.[idx]?.distance || 0).toFixed(2)}米</span>
            <div
                style={{
                    flex: 1,
                    backgroundColor: tokens.colorBrandBackground2,
                    position: "relative",
                }}
            >
                {items.map((frame, i) => {
                    return (
                        <div
                            className={styles.slice}
                            style={{
                                backgroundColor: frame.checked
                                    ? "green"
                                    : tokens.colorBrandBackground,
                                left: `${
                                    (frame.distance /
                                        rawData?.leaf?.model?.length) *
                                    100
                                }%`,
                            }}
                            key={i}
                            onClick={() => setIdx(i)}
                        />
                    );
                })}
            </div>
            <span></span>
            <span></span>
            <span></span>
            <div
                style={{
                    flex: 1,
                    position: "relative",
                    color: tokens.colorBrandBackground,
                }}
            >
                <IC.TriangleUpFilled
                    style={{
                        position: "absolute",
                        left: `calc(${(
                            (items?.[idx]?.distance /
                                rawData?.leaf?.model?.length) *
                            100
                        ).toFixed(2)}% - 5px)`,
                    }}
                />
            </div>
        </div>
    );

    return (
        <div className={styles.root}>
            {middle}
            {track}
        </div>
    );
};
